import { CallerMemory, RecentInteraction } from "./types";

function normalizeIndexedCollection<T>(
	value: unknown,
	isValid: (item: unknown) => item is T,
): T[] {
	if (Array.isArray(value)) {
		return value.filter(isValid);
	}
	if (!value || typeof value !== "object") {
		return [];
	}

	return Object.entries(value)
		.sort(([left], [right]) => {
			const leftIndex = Number(left);
			const rightIndex = Number(right);
			const leftIsIndex = Number.isInteger(leftIndex) && leftIndex >= 0;
			const rightIsIndex = Number.isInteger(rightIndex) && rightIndex >= 0;

			if (leftIsIndex && rightIsIndex) return leftIndex - rightIndex;
			if (leftIsIndex) return -1;
			if (rightIsIndex) return 1;
			return left.localeCompare(right);
		})
		.map(([, item]) => item)
		.filter(isValid);
}

function isString(item: unknown): item is string {
	return typeof item === "string";
}

function isRecentInteraction(item: unknown): item is RecentInteraction {
	return item !== null && typeof item === "object";
}

/**
 * Normalizes legacy Firestore maps with numeric string keys to the array-based
 * caller memory contract used by the backend and UIs.
 */
export function normalizeCallerMemory(
	value: unknown,
): CallerMemory | undefined {
	if (!value || typeof value !== "object") {
		return undefined;
	}

	const { openItems, ...memory } = value as CallerMemory & {
		openItems?: unknown;
	};
	const facts = normalizeIndexedCollection(memory.facts, isString);
	const legacyOpenItems = normalizeIndexedCollection(openItems, isString);
	return {
		...memory,
		facts: [...facts, ...legacyOpenItems],
		preferences: normalizeIndexedCollection(memory.preferences, isString),
		recentInteractions: normalizeIndexedCollection(
			memory.recentInteractions,
			isRecentInteraction,
		),
	};
}
